import {
	TCreateFilterViewRequest,
	TCustomer,
	TFilterView,
	TSmartVariableDefinition,
} from '@/types/TSmartCRM';
import { EViewType, ETabs } from 'src/enums/ESmartCRM';
import { fetchFilteredLeads } from '@/utils/SmartCRM/fieldUtils';
import {
	resetPaginationState,
	updatePaginationFromResponse,
} from '@/utils/SmartCRM/paginationUtils';
import { createFilterView, deleteFilterView, getFilterViews } from '@/api/crm/smartCRM';

export interface FilterView {
	id: string;
	name: string;
	filterGroups: any;
	groupNames?: string[];
	isActive: boolean;
	customerCount?: number;
	filteredCustomers?: TCustomer[];
	responseGroups?: Record<string, TCustomer[]>;
}

// export const createFilterView = (name: string, filters: any, groupNames?: string[], leads: TCustomer[] = [], count = 0): FilterView => ({
// 	id: `view_${Date.now()}`,
// 	name,
// 	filterGroups: filters,
// 	groupNames,
// 	isActive: false,
// 	customerCount: count,
// 	filteredCustomers: leads,
// });

export const handleSaveAsView = async (
	name: string,
	filters: any,
	selectedAgentId: string,
	view: EViewType,
	viewId?: string,
): Promise<TFilterView> => {
	const result = await fetchFilteredLeads('', selectedAgentId, view, { limit: 20 }, filters);

	const filterViewData: TCreateFilterViewRequest = {
		name,
		agentId: selectedAgentId,
		filterGroups: filters,
		view,
		customerCount: result?.count || 0,
		...(viewId && { viewId }),
	};

	const response = await createFilterView(filterViewData);

	if (response.success && response.data) {
		const savedView = response.data.filterView;
		return {
			...savedView,
			isActive: false,
			filteredCustomers: result?.leads || [],
			responseGroups: result?.groups || undefined,
		};
	} else {
		throw new Error(response.message || 'Failed to save filter view');
	}
};

export const loadFilterViews = async (agentId: string): Promise<TFilterView[]> => {
	const response = await getFilterViews(agentId);
	if (response.success && response.data) {
		return (
			response.data.filterViews.map((view) => ({
				...view,
				isActive: false,
				filteredCustomers: [],
				responseGroups: undefined,
			})) || []
		);
	}
	return [];
};

export const removeFilterView = async (agentId: string, viewId: string): Promise<boolean> => {
	const response = await deleteFilterView(agentId, viewId);
	if (response.success) {
		return true;
	}
	throw new Error(response.message || 'Failed to delete filter view');
};

export const handleFilteredLeads = async (
	fieldId: string,
	selectedAgentId: string,
	view: EViewType,
	pageSize: number,
	setFilterLoading: (loading: boolean) => void,
	setFilteredCustomers: (customers: TCustomer[]) => void,
	setFilteredCount: (count: number) => void,
	setFilteredPagination: (updateFn: (prev: any) => any) => void,
	filters?: any,
) => {
	if (!selectedAgentId) {
		return;
	}

	setFilterLoading(true);
	try {
		const result = await fetchFilteredLeads(
			fieldId,
			selectedAgentId,
			view,
			{ limit: pageSize, lastEvaluatedKey: null },
			filters,
		);

		if (result) {
			setFilteredCustomers(result.leads);
			setFilteredCount(result.count);
			setFilteredPagination((prev) =>
				updatePaginationFromResponse(resetPaginationState(prev), result.pagination, pageSize),
			);
		} else {
			setFilteredCustomers([]);
			setFilteredCount(0);
			setFilteredPagination((prev) => resetPaginationState(prev));
		}
	} catch {
		setFilteredCustomers([]);
		setFilteredCount(0);
		setFilteredPagination((prev) => resetPaginationState(prev));
	} finally {
		setFilterLoading(false);
	}
};

export const createPaginationHandlers = (
	filteredPagination: any,
	groupBy: string,
	fields: TSmartVariableDefinition[],
	selectedAgentId: string,
	view: EViewType,
	setFilterLoading: (loading: boolean) => void,
	setFilteredCustomers: (customers: TCustomer[]) => void,
	setFilteredCount: (count: number) => void,
	setFilteredPagination: (updateFn: (prev: any) => any) => void,
) => {
	const getSelectedField = () => fields.find((field) => field.internalKey === groupBy);

	const nextPage = () => {
		if (filteredPagination.hasNext && filteredPagination.nextKey && groupBy) {
			const selectedField = getSelectedField();
			if (selectedField?.id) {
				const currentKey =
					filteredPagination.prevKeys.length > 0
						? filteredPagination.prevKeys[filteredPagination.prevKeys.length - 1]
						: null;

				setFilteredPagination((prev) => ({ ...prev, prevKeys: [...prev.prevKeys, currentKey] }));
				setFilterLoading(true);

				fetchFilteredLeads(selectedField.id, selectedAgentId, view, {
					limit: filteredPagination.pageSize,
					lastEvaluatedKey: filteredPagination.nextKey,
				})
					.then((result) => {
						if (result) {
							setFilteredCustomers(result.leads);
							setFilteredCount(result.count);
							setFilteredPagination((prev) =>
								updatePaginationFromResponse(prev, result.pagination, prev.pageSize),
							);
						}
					})
					.finally(() => setFilterLoading(false));
			}
		}
	};

	const prevPage = () => {
		if (filteredPagination.prevKeys.length > 0 && groupBy) {
			const selectedField = getSelectedField();
			if (selectedField?.id) {
				const newPrevKeys = [...filteredPagination.prevKeys];
				const prevKey = newPrevKeys.pop();

				setFilteredPagination((prev) => ({ ...prev, prevKeys: newPrevKeys }));
				setFilterLoading(true);

				fetchFilteredLeads(selectedField.id, selectedAgentId, view, {
					limit: filteredPagination.pageSize,
					lastEvaluatedKey: prevKey || null,
				})
					.then((result) => {
						if (result) {
							setFilteredCustomers(result.leads);
							setFilteredCount(result.count);
							setFilteredPagination((prev) =>
								updatePaginationFromResponse(prev, result.pagination, prev.pageSize),
							);
						}
					})
					.finally(() => setFilterLoading(false));
			}
		}
	};

	const changePageSize = (newSize: number) => {
		if (groupBy) {
			const selectedField = getSelectedField();
			if (selectedField?.id) {
				setFilteredPagination((prev) => ({ ...prev, pageSize: newSize, prevKeys: [] }));
				setFilterLoading(true);

				fetchFilteredLeads(selectedField.id, selectedAgentId, view, {
					limit: newSize,
					lastEvaluatedKey: null,
				})
					.then((result) => {
						if (result) {
							setFilteredCustomers(result.leads);
							setFilteredCount(result.count);
							setFilteredPagination((prev) =>
								updatePaginationFromResponse(
									resetPaginationState(prev),
									result.pagination,
									newSize,
								),
							);
						}
					})
					.finally(() => setFilterLoading(false));
			}
		}
	};

	return { nextPage, prevPage, changePageSize };
};

export const handleViewChange = (
	newView: EViewType,
	currentView: EViewType,
	groupBy: string,
	activeFilterView: string | null,
	groupByOptions: Array<{ value: string; label: string }>,
	fields: TSmartVariableDefinition[],
	setView: (view: EViewType) => void,
	setGroupBy: (groupBy: string) => void,
	handleFilteredLeadsCallback: (fieldId: string) => void,
) => {
	setView(newView);

	if (newView === EViewType.TABLE && currentView === EViewType.BOARD) {
		return;
	}

	if (
		newView === EViewType.BOARD &&
		(!groupBy || groupBy === '') &&
		!activeFilterView &&
		groupByOptions.length > 0
	) {
		const firstFieldKey = groupByOptions[0].value;
		setGroupBy(firstFieldKey);

		const firstField = fields.find((field) => field.internalKey === firstFieldKey);
		if (firstField?.id) {
			handleFilteredLeadsCallback(firstField.id);
		}
	}
};

export const handleGroupByChange = async (
	value: string,
	fields: TSmartVariableDefinition[],
	pageSize: number,
	setGroupBy: (groupBy: string) => void,
	setFilteredCustomers: (customers: TCustomer[]) => void,
	setFilteredCount: (count: number) => void,
	setFilteredPagination: (updateFn: (prev: any) => any) => void,
	fetchLeads: (pageSize: number, key: any) => void,
	handleFilteredLeadsCallback: (fieldId: string) => void,
) => {
	const newGroupBy = value || '';
	setGroupBy(newGroupBy);

	if (!newGroupBy || newGroupBy === 'all') {
		setFilteredCustomers([]);
		setFilteredCount(0);
		setFilteredPagination((prev) => resetPaginationState(prev));
		fetchLeads(pageSize, null);
		return;
	}

	const selectedField = fields.find((field) => field.internalKey === newGroupBy);
	if (selectedField?.id) {
		handleFilteredLeadsCallback(selectedField.id);
	}
};

export const handleFilterViewChange = async (
	viewId: string | null,
	filterViews: FilterView[],
	selectedAgentId: string,
	view: EViewType,
	setActiveFilterView: (viewId: string | null) => void,
	setActiveTab: (tab: ETabs) => void,
	setGroupBy: (groupBy: string) => void,
	setFilterViews: (updateFn: (prev: FilterView[]) => FilterView[]) => void,
) => {
	setActiveFilterView(viewId);
	if (viewId) {
		setActiveTab(ETabs.CUSTOMERS);
		setGroupBy('');

		const activeView = filterViews.find((v) => v.id === viewId);
		if (activeView) {
			try {
				const result = await fetchFilteredLeads(
					'',
					selectedAgentId,
					view,
					{ limit: 20 },
					activeView.filterGroups,
				);
				if (result) {
					setFilterViews((prev) =>
						prev.map((view) =>
							view.id === viewId
								? {
										...view,
										filteredCustomers: result.leads,
										customerCount: result.count || result.leads.length,
									}
								: view,
						),
					);
				}
			} catch {
				// Error handling - graceful degradation
			}
		}
	}
};

export const getDisplayCustomers = (
	activeFilterView: string | null,
	filterViews: FilterView[],
	groupBy: string,
	filteredCustomers: TCustomer[],
	customers: TCustomer[],
): TCustomer[] => {
	if (activeFilterView) {
		const activeView = filterViews.find((v) => v.id === activeFilterView);
		return activeView?.filteredCustomers || [];
	}
	return groupBy ? filteredCustomers : customers;
};

export const getCurrentPagination = (
	activeFilterView: string | null,
	groupBy: string,
	filteredPagination: any,
	pagination: any,
	paginationHandlers: any,
) => {
	if (activeFilterView) {
		return undefined;
	}
	if (groupBy) {
		return { ...filteredPagination, ...paginationHandlers };
	}
	return pagination;
};

export const getCurrentGroupName = (
	filterViews: FilterView[],
	activeFilterView: string | null,
): string[] | null => {
	if (activeFilterView) {
		const activeView = filterViews.find((v) => v.id === activeFilterView);
		return activeView?.groupNames || null;
	}
	return null;
};

export const searchCustomers = (customers: TCustomer[], searchValue: string): TCustomer[] => {
	if (!searchValue?.trim()) {
		return customers;
	}

	const searchLower = searchValue.toLowerCase();

	return customers.filter((customer) => {
		const nameMatch = customer.name?.toLowerCase().includes(searchLower);
		const emailMatch = customer.email?.toLowerCase().includes(searchLower);
		const phoneMatch = customer.number?.toLowerCase().includes(searchLower);

		const variableMatch = customer.variables?.some((variable) => {
			const fieldNameMatch = variable.fieldName?.toLowerCase().includes(searchLower);
			const valueMatch =
				variable.value && String(variable.value).toLowerCase().includes(searchLower);
			return fieldNameMatch || valueMatch;
		});

		return nameMatch || emailMatch || phoneMatch || variableMatch;
	});
};
